import { motion } from "framer-motion";
import { CakeSlice, Cookie, Heart, MapPin } from "lucide-react";
import { useSiteContent } from "@/lib/content";

const FEATURES = [
  { icon: CakeSlice, title: "Custom Cakes", text: "Birthdays, weddings and every milestone in between — baked and dressed to your theme." },
  { icon: Heart, title: "Cupcakes", text: "Mini or regular, buttercream blooms to fondant toppers, by the dozen or by the hundred." },
  { icon: Cookie, title: "Cookies", text: "Hand-decorated cookies that match your cake, your colours and your celebration." },
];

export default function About() {
  const content = useSiteContent();
  const text = content?.text || {};
  return (
    <section id="about" data-testid="about-section" className="relative mx-auto max-w-6xl px-4 pb-28 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-[2rem] border border-gold/40 bg-white/75 p-8 shadow-[0_24px_70px_-24px_rgba(123,45,78,0.35)] backdrop-blur sm:p-14"
      >
        <img src="/brand/floral-br.png" alt="" aria-hidden className="pointer-events-none absolute -bottom-10 -right-10 w-52 opacity-70" />

        <p className="font-script text-2xl text-gold">A little about me</p>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-wine">
          {text.about_heading || "About Miss Cupcake"}
        </h2>

        <div className="mt-8 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <div className="shrink-0 text-center">
            <img
              src="/brand/baker.jpg"
              alt="The baker behind Miss Cupcake Creations"
              data-testid="about-baker-photo"
              className="h-36 w-36 rounded-full border-2 border-gold/60 object-cover shadow-[0_16px_40px_-14px_rgba(123,45,78,0.5)] sm:h-44 sm:w-44"
            />
            <p className="mt-3 font-script text-xl text-gold">Miss Cupcake herself</p>
          </div>
          <p className="max-w-2xl font-script text-3xl sm:text-4xl leading-snug text-burgundy" data-testid="about-tagline">
            “{text.about_tagline || "Celebrate it uniquely — Custom Cakes, Cupcakes and Cookies, Hillcrest/Logan"}”
          </p>
        </div>

        <p className="mt-6 max-w-2xl text-sm sm:text-base leading-relaxed text-plum/75">
          {text.about_paragraph || "Every celebration deserves something made just for it. From my home kitchen in Hillcrest, I create custom sweets made to order — trusted by many returning customers and chosen for special moments, again and again. Tell me your theme, your colours and your occasion, and together we will make it unforgettable."}
        </p>

        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 + i * 0.12, duration: 0.7 }}
              className="rounded-2xl border border-gold/30 bg-blush/80 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/60"
              data-testid={`about-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <f.icon className="mb-3 h-6 w-6 text-gold" />
              <h3 className="font-serif text-xl font-semibold text-burgundy">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-plum/70">{f.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex items-center gap-2 text-sm text-wine/80">
          <MapPin className="h-4 w-4 text-gold" />
          {text.about_location || "Baking for Hillcrest, Logan and surrounds — pickup or delivery by arrangement."}
        </div>
      </motion.div>
    </section>
  );
}
